import styles from './index.module.css'

import debounce from 'lodash/debounce'
import * as React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'

import {
  campaignRecipientAdd,
  campaignRecipientChange,
  campaignRecipientModeSelect,
  campaignRecipientRemove,
} from '@disco/state-management/actions/campaign-recipient-actions'
import {
  loadMailingLists,
  loadRuleMailingLists,
} from '@disco/state-management/actions/EmailGroupActions'
import type { MatchMode } from '@disco/state-management/reducers/email-campaigns/recipient-rules'
import { getCampaignRules } from '@disco/state-management/selectors/campaigns'
import {
  getDeletedEmailGroupIds,
  getMailingLists,
} from '@disco/state-management/selectors/emailGroups'

import type { State } from 'types/redux'

import TagSelector from 'components/common/tag-selector'
import ChoiceList from 'components/ui/choice-list'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import Label from 'components/ui/label'
import TextField from 'components/ui/text-field'

import CloseBtn from './close-btn'

function mapStateToProps(state: State) {
  return {
    mailingLists: getMailingLists(state),
    deletedListIds: getDeletedEmailGroupIds(state),
    recipientRules: getCampaignRules(state),
  }
}

const mapActionsToProps = {
  loadMailingLists,
  loadRuleMailingLists,
  campaignRecipientModeSelect,
  campaignRecipientAdd,
  campaignRecipientRemove,
  campaignRecipientChange,
}
const defaultRuleOps: RuleOp[] = [
  'equals',
  'notEquals',
  'contains',
  'notContains',
]
const ruleOpMapping: Record<RuleOp, string> = {
  equals: 'is',
  notEquals: 'is not',
  contains: 'contains',
  notContains: 'does not contain',
}
type RuleOp = 'equals' | 'notEquals' | 'contains' | 'notContains'
type RuleType = {
  label: string
  ruleType: string
  ruleOps: RuleOp[]
  deprecated?: boolean
  icon?: string
}
const rules: RuleType[] = [
  {
    label: 'Mailing list',
    ruleType: 'mailingList',
    ruleOps: defaultRuleOps,
    icon: 'playlist-add',
  },
  {
    label: 'Name',
    ruleType: 'name',
    ruleOps: defaultRuleOps,
    icon: 'contact-mail',
  },
  {
    label: 'Company',
    ruleType: 'company',
    ruleOps: defaultRuleOps,
    icon: 'business-center',
  },
  {
    label: 'Email',
    ruleType: 'email',
    ruleOps: defaultRuleOps,
    icon: 'send',
  },
  {
    label: 'Location',
    ruleType: 'territory',
    ruleOps: defaultRuleOps,
    icon: 'pin-circle',
  },
  {
    label: 'Contact tag',
    ruleType: 'tags',
    ruleOps: ['equals', 'notEquals'],
    icon: 'playlist-add',
  },
  {
    // this rule type is left for backwards compatibility
    label: 'Contact type',
    ruleType: 'type',
    ruleOps: defaultRuleOps,
    deprecated: true,
  },
]
const availableRules = rules.filter(r => !r.deprecated)
const modes = [
  {
    label: 'All these rules',
    value: 'all',
  },
  {
    label: 'Any of these rules',
    value: 'any',
  },
]

type Props = {
  recipientRules: State['emailCampaigns']['recipientRules']
  mailingLists: ReturnType<typeof getMailingLists>
  deletedListIds: ReturnType<typeof getDeletedEmailGroupIds>
  campaignRecipientModeSelect: typeof campaignRecipientModeSelect
  campaignRecipientAdd: typeof campaignRecipientAdd
  campaignRecipientRemove: typeof campaignRecipientRemove
  campaignRecipientChange: typeof campaignRecipientChange
  loadMailingLists: typeof loadMailingLists
  loadRuleMailingLists: typeof loadRuleMailingLists
}

class CampaignRecipientRules extends React.Component<Props> {
  componentDidMount() {
    this.props.loadMailingLists()
    this.props.loadRuleMailingLists()
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.recipientRules.rules !== this.props.recipientRules.rules) {
      this.props.loadRuleMailingLists()
    }
  }

  _handleChangeMode = (key: MatchMode, value: boolean) => {
    this.props.campaignRecipientModeSelect(key)
  }
  _handleChangeRuleMode = (ruleId, ruleOp) => {
    this.props.campaignRecipientChange(ruleId, {
      ruleOp,
    })
  }
  _handleChangeRuleValue = debounce((ruleId, ruleValue) => {
    this.props.campaignRecipientChange(ruleId, {
      ruleValue,
    })
  }, 300)
  _onChange = (evt: React.SyntheticEvent<HTMLInputElement>) => {
    const ruleId = evt.currentTarget.name
    const ruleValue = evt.currentTarget.value

    this._handleChangeRuleValue(ruleId, ruleValue)
  }
  _onTagsSelect = (ruleId, tag) => {
    this._handleChangeRuleValue(ruleId, tag ? tag.value : '')
  }
  _handleRemoveRule = ruleId => {
    this.props.campaignRecipientRemove(ruleId)
  }
  _handleAddRule = ruleType => {
    this.props.campaignRecipientAdd(ruleType)
  }

  _renderModeSelect() {
    const { recipientRules } = this.props
    return (
      <div className={styles.campaignRecipientRulesRuleCont}>
        <ChoiceList
          multiple={false}
          title="Users who match"
          items={modes}
          selected={recipientRules.matchMode}
          onChange={this._handleChangeMode}
        />
      </div>
    )
  }

  _renderMailingListRule(rule) {
    const availableRule = rules.find(r => r.ruleType === rule.ruleType)
    const label = availableRule ? availableRule.label : 'Unknown'
    const isDeleted = this.props.deletedListIds.includes(
      parseInt(rule.ruleValue),
    )
    return (
      <div key={rule.id} className={styles.campaignRecipientRulesRuleCont}>
        <div className={styles.campaignRecipientRulesCloseBtn}>
          <CloseBtn onClick={() => this._handleRemoveRule(rule.id)} />
        </div>
        <Label caption={label} />
        <CustomDropdown
          options={this.props.mailingLists.map(item => ({
            label: item.name,
            value: item.id.toString(),
          }))}
          multi={false}
          clearable={false}
          // a deleted list isn't in the options, so the raw id would show
          value={isDeleted ? '' : rule.ruleValue}
          onChange={ruleValue => {
            this.props.campaignRecipientChange(rule.id, {
              ruleValue: ruleValue.value,
            })
          }}
        />
        <div className={styles.campaignRecipientRulesListTip}>
          {isDeleted ? (
            'This mailing list was deleted. Choose another list or remove this rule.'
          ) : (
            <>
              Need to set up your mailing lists?&nbsp;
              <Link to="/lists">Do that here.</Link>
            </>
          )}
        </div>
      </div>
    )
  }

  _renderRule(rule) {
    if (rule.ruleType === 'mailingList')
      return this._renderMailingListRule(rule)
    const availableRule = rules.find(r => r.ruleType === rule.ruleType)
    const label = availableRule ? availableRule.label : 'Unknown'
    const options = availableRule && (
      <ChoiceList
        multiple={false}
        title={label}
        items={availableRule.ruleOps.map(op => ({
          label: ruleOpMapping[op],
          value: op,
        }))}
        selected={rule.ruleOp}
        onChange={value => this._handleChangeRuleMode(rule.id, value)}
      />
    )
    const input =
      rule.ruleType !== 'tags' ? (
        <TextField
          name={rule.id}
          defaultValue={rule.ruleValue}
          onChange={this._onChange}
        />
      ) : (
        <TagSelector
          onChange={tags => this._onTagsSelect(rule.id, tags)}
          selectedTags={rule.ruleValue ? rule.ruleValue : ''}
          placeholder="Select tag"
          tagsType="contact"
          multi={false}
        />
      )
    return (
      <div key={rule.id} className={styles.campaignRecipientRulesRuleCont}>
        <div className={styles.campaignRecipientRulesCloseBtn}>
          <CloseBtn onClick={() => this._handleRemoveRule(rule.id)} />
        </div>
        {options}
        {input}
      </div>
    )
  }

  _renderAdding() {
    return (
      <div className={styles.campaignRecipientRulesRuleCont}>
        <EllipsisMenu
          className={styles.campaignRecipientRulesEllipsisMenu}
          caption="Add a rule"
          compact={true}
          icon="pen"
          options={availableRules.map(rule => ({
            label: rule.label,
            value: rule.ruleType,
            icon: rule.icon,
          }))}
          onOptionClick={this._handleAddRule}
        />
      </div>
    )
  }

  render() {
    return (
      <div className={styles.campaignRecipientRules}>
        <div className={styles.campaignRecipientRulesCont}>
          {this._renderModeSelect()}
          {this.props.recipientRules.rules.map(rule => this._renderRule(rule))}
          {this._renderAdding()}
        </div>
      </div>
    )
  }
}

export default connect(
  mapStateToProps,
  mapActionsToProps,
)(CampaignRecipientRules) as any
